import { AdEventKey, EventKey, PlaybackState, } from '../enums';
import { PlaybackError } from '../../errors';
import { ErrorCode } from '../../errors/enums/error-code';
import { EventKey as TelemetryEventKey } from '../../telemetry/enums/event-key';
import { mapAudioTrackFormat } from '../../tracks/utils';
export const WithEvent = (Base) => {
    return class PlayerWithEvent extends Base {
        listeners = new Map();
        internalListeners = new Map();
        timeouts = new WeakMap();
        timingReadyTriggered = false;
        waitForTimingEvent = {
            seekableRange: true,
            position: true,
        };
        get waitForTimingReady() {
            return Object.values(this.waitForTimingEvent).some((v) => v);
        }
        resetWaitForTimingReady() {
            this.waitForTimingEvent = {
                seekableRange: true,
                position: true,
            };
            this.timingReadyTriggered = false;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        constructor(...args) {
            super(...args);
            this.attachAdapterCallbacks();
        }
        triggerFullEvent(event, options) {
            if (!options?.internal) {
                const handler = this.listeners.get(event.type);
                handler?.forEach((listener) => {
                    listener(event);
                });
            }
            const internalHandler = this.internalListeners.get(event.type);
            internalHandler?.forEach((listener) => {
                listener(event);
            });
        }
        triggerEvent(payload) {
            const psid = payload.psid ?? this.psid;
            if (payload.type === AdEventKey.AD_METADATA) {
                this._logger?.info('METADATA', JSON.stringify(payload));
            }
            if (!psid) {
                // Skip missing psid error for 2nd time
                if (payload.type === EventKey.PLAYER_ERROR &&
                    payload.error.code !== ErrorCode.MissingPsid) {
                    this.handleError(new PlaybackError(ErrorCode.MissingPsid, `psid is not set on event ${payload.type}`));
                }
                return;
            }
            const event = {
                ...payload,
                psid,
            };
            this.triggerFullEvent(event);
        }
        on(eventKey, listener) {
            if (!this.listeners.has(eventKey)) {
                this.listeners.set(eventKey, []);
            }
            this.listeners.get(eventKey)?.push(listener);
        }
        onInternal(eventKey, listener) {
            if (!this.internalListeners.has(eventKey)) {
                this.internalListeners.set(eventKey, []);
            }
            this.internalListeners.get(eventKey)?.push(listener);
        }
        ephemeral(eventKey, listener, timeout) {
            const handler = setTimeout(() => {
                this.off(eventKey, listener);
            }, timeout);
            this.timeouts.set(listener, handler);
            this.on(eventKey, listener);
        }
        off(eventKey, listener) {
            const timeoutHandler = this.timeouts.get(listener);
            if (timeoutHandler) {
                clearTimeout(timeoutHandler);
                this.timeouts.delete(listener);
            }
            const eventListeners = this.listeners.get(eventKey);
            if (!eventListeners) {
                return;
            }
            this.listeners.set(eventKey, eventListeners.filter((l) => l !== listener));
        }
        offInternal(eventKey, listener) {
            const eventListeners = this.internalListeners.get(eventKey);
            if (!eventListeners) {
                return;
            }
            this.internalListeners.set(eventKey, eventListeners.filter((l) => l !== listener));
        }
        attachAdapterCallbacks() {
            this.adapter.onPlaybackDurationChanged = (duration) => {
                this._duration = duration;
            };
            this.adapter.onPlayerReady = () => {
                this.triggerEvent({
                    type: EventKey.PLAYER_READY,
                });
            };
            this.adapter.onSeekableRangeChanged = (seekableRange) => {
                this.waitForTimingEvent.seekableRange = false;
                if (!this.waitForTimingReady &&
                    seekableRange &&
                    !this.timingReadyTriggered) {
                    this.timingReadyTriggered = true;
                    this.triggerEvent({
                        type: EventKey.PLAYER_TIMING_READY,
                        data: {
                            position: this.getCurrentPosition() ?? 0,
                            seekableRange,
                        },
                    });
                }
                this.setPlayerState([{
                        seekableRange,
                    }]);
            };
            this.adapter.onPositionChanged = (position) => {
                if (this.seekPosition != null) {
                    return;
                }
                this.waitForTimingEvent.position = false;
                if (!this.waitForTimingReady &&
                    this._seekableRange &&
                    !this.timingReadyTriggered) {
                    this.timingReadyTriggered = true;
                    this.triggerEvent({
                        type: EventKey.PLAYER_TIMING_READY,
                        data: {
                            position,
                            seekableRange: this._seekableRange,
                        },
                    });
                }
                this.setCurrentPosition(position);
                this.setPlayerState([]);
                this.triggerEvent({
                    type: EventKey.CURRENT_POSITION_CHANGED,
                    position,
                });
            };
            this.adapter.onAvailableAudioTracksChanged = (audioTracks) => {
                const mappedAudioTracks = audioTracks.map((track) => mapAudioTrackFormat(track));
                this.triggerEvent({
                    type: EventKey.AVAILABLE_AUDIO_TRACKS_CHANGED,
                    tracks: mappedAudioTracks,
                });
            };
            this.adapter.onAvailableSubtitlesTracksChanged = (subtitleTracks) => {
                this.triggerEvent({
                    type: EventKey.AVAILABLE_SUBTITLES_TRACKS_CHANGED,
                    tracks: subtitleTracks,
                });
            };
            this.adapter.onSeeked = (position) => {
                const currentPosition = this.getCurrentPosition() ?? 0;
                const seekedTime = position - currentPosition;
                this.setCurrentPosition(position);
                this.triggerEvent({
                    type: EventKey.SEEKED,
                    position,
                    seekedTime,
                });
            };
            this.adapter.onSelectedSubtitlesTrack = (targetTrack) => {
                this.triggerEvent({
                    type: EventKey.SELECTED_SUBTITLES_TRACK_CHANGED,
                    targetTrack,
                });
            };
            this.adapter.onSelectedAudioTrackChanged = (targetTrack) => {
                const mappedTrack = targetTrack ?
                    mapAudioTrackFormat(targetTrack)
                    : targetTrack;
                this.triggerEvent({
                    type: EventKey.SELECTED_AUDIO_TRACK_CHANGED,
                    targetTrack: mappedTrack,
                });
            };
            this.adapter.onCastMediaTracksChanged = (data) => {
                this.triggerEvent({
                    type: EventKey.CAST_TRACKS_CHANGED,
                    data,
                });
            };
            this.adapter.onCastMediaTrackSelectionChanged = (data) => {
                this.triggerEvent({
                    type: EventKey.CAST_TRACK_SELECTION_CHANGED,
                    data,
                });
            };
            this.adapter.onPlaybackStateChanged = (state) => {
                this._playbackState = state;
                if (!this.waitForTimingReady &&
                    state === PlaybackState.STOPPED) {
                    // Reset when stream stopped so it can be triggered again on next load
                    this.resetWaitForTimingReady();
                }
                if (this._playbackStateInitial &&
                    state === PlaybackState.PLAYING) {
                    this._playbackStateInitial = false;
                }
                this.triggerEvent({
                    type: EventKey.PLAYBACK_STATE_CHANGED,
                    state,
                });
                this.setPlayerState([{}]);
            };
            this.adapter.onVideoBitrateChanged = (bitrate) => {
                this._bitrate = bitrate;
                this.triggerEvent({
                    type: EventKey.QUALITY_CHANGED,
                    bitrate,
                });
            };
            this.adapter.onError = (error) => {
                this.handleError(error);
            };
            this.adapter.onGeometryChanged = (geometry) => {
                this._geometry = geometry;
                if (!this.psid) {
                    return;
                }
                this.triggerTelemetryEvent({
                    type: TelemetryEventKey.GEOMETRY_CHANGED,
                    psid: this.psid,
                    player_size_w: geometry.playerWidth ?? 0,
                    player_size_h: geometry.playerHeight ?? 0,
                    screen_size_w: geometry.screenWidth ?? 0,
                    screen_size_h: geometry.screenHeight ?? 0,
                });
            };
            this.adapter.onBandwidthMeasured = (bandwidth) => {
                this._bandwidth = bandwidth;
            };
            this.adapter.onPlaybackEnded = () => {
                if (!this.psid) {
                    return;
                }
                this.triggerTelemetryEvent({
                    type: TelemetryEventKey.ENDED,
                    psid: this.psid,
                });
                this.triggerEvent({
                    type: EventKey.MEDIA_ENDED,
                });
            };
            this.adapter.onPlay = () => {
                if (!this.psid) {
                    return;
                }
                this.triggerTelemetryEvent({
                    type: TelemetryEventKey.PLAY,
                    psid: this.psid,
                });
            };
            this.adapter.onLatencyChanged = (metadata) => {
                this.triggerEvent({
                    type: EventKey.LATENCY_CHANGED,
                    metadata,
                });
            };
            this.adapter.onDroppedFrames = (droppedFrames, elapsedMs) => {
                this.triggerEvent({
                    type: EventKey.DROPPED_FRAMES,
                    droppedFrames,
                    elapsedMs,
                });
            };
            this.adapter.onAdBreakStarted = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_BREAK_STARTED,
                    data,
                });
            };
            this.adapter.onAdMetadata = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_METADATA,
                    data,
                });
            };
            this.adapter.onAdBreakEnded = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_BREAK_ENDED,
                    data,
                });
            };
            this.adapter.onAdLoaded = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_LOADED,
                    data,
                });
            };
            this.adapter.onAdStarted = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_STARTED,
                    data,
                });
            };
            this.adapter.onAdPaused = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_PAUSED,
                    data,
                });
            };
            this.adapter.onAdResumed = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_RESUMED,
                    data,
                });
            };
            this.adapter.onAdPositionChanged = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_POSITION_CHANGED,
                    data,
                });
            };
            this.adapter.onAdFirstQuartile = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_FIRST_QUARTILE,
                    data,
                });
            };
            this.adapter.onAdMidpoint = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_MIDPOINT,
                    data,
                });
            };
            this.adapter.onAdThirdQuartile = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_THIRD_QUARTILE,
                    data,
                });
            };
            this.adapter.onAdEnded = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_ENDED,
                    data,
                });
            };
            this.adapter.onAdSkipped = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_SKIPPED,
                    data,
                });
            };
            this.adapter.onAdError = (data) => {
                this.triggerEvent({
                    type: AdEventKey.AD_ERROR,
                    data,
                });
            };
            this.adapter.onPauseAdParsed = (data) => {
                this.triggerEvent({
                    type: AdEventKey.PAUSE_AD_PARSED,
                    data,
                });
            };
            this.adapter.onAdSkipAvailable = () => {
                this.triggerEvent({
                    type: AdEventKey.AD_BREAK_SKIP_AVAILABLE,
                });
            };
            this.adapter.onAdSkipUnavailable = () => {
                this.triggerEvent({
                    type: AdEventKey.AD_BREAK_SKIP_UNAVAILABLE,
                });
            };
        }
    };
};
